import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Confirmation that something happened.
 *
 * Every screen used to push a green banner into the top of the page after a
 * save, which moved everything under the person's cursor at the moment they
 * were most likely to click again. A toast sits over the corner instead, says
 * what happened in the same words as the button that did it, and leaves.
 *
 * It does not carry errors. Those belong beside the field or the form that
 * caused them, where the person can act on them, and they must not disappear
 * on a timer.
 */

interface Toast {
  id: number;
  message: string;
  detail?: string;
}

const ToastContext = createContext<(message: string, detail?: string) => void>(() => undefined);

export const useToast = (): ((message: string, detail?: string) => void) => useContext(ToastContext);

export function ToastHost({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, detail?: string) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, detail }]);
    // Long enough to read a document number off it, short enough not to nag.
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 5000);
  }, []);

  const value = useMemo(() => show, [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="no-print pointer-events-none fixed bottom-4 right-4 z-50 flex w-80 flex-col gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-auto rounded-md border-l-2 border-success bg-surface px-3.5 py-2.5 shadow-pop
                       motion-safe:animate-[toastIn_.18s_ease-out]"
            role="status"
          >
            <p className="text-sm font-medium">{t.message}</p>
            {t.detail ? <p className="num mt-0.5 text-2xs text-ink-soft">{t.detail}</p> : null}
            <button
              className="absolute right-2 top-2 text-2xs text-ink-faint hover:text-ink"
              onClick={() => setToasts((c) => c.filter((x) => x.id !== t.id))}
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
